import React, { useEffect, useState } from "react";
import { Paper, Grid, Box, Button, Typography, TextField, Select, MenuItem, FormControl, InputLabel, Card, CardContent, CardActions, Switch, Chip, Pagination, InputAdornment } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import AddRow from "./AddRow";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import { getCampUrls, getCodeType, getSiteId, updateCampUrls } from "../../redux/features/clicksSlice";
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';

const EditableField = ({ label, value, field, type="text", options=[], onSave, displayValue, forceEdit=false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(value || "");

  useEffect(() => {
    setCurrentValue(value || "");
  }, [value]);

  useEffect(() => {
    if (forceEdit) {
      setIsEditing(true);
    } else {
      if (isEditing && currentValue != (value || "")) {
        onSave(field, currentValue);
      }
      setIsEditing(false);
    }
  }, [forceEdit]);

  const handleBlur = async () => {
    setIsEditing(false);
    if (currentValue != (value || "")) {
      await onSave(field, currentValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  if (isEditing) {
    if (type === "select") {
      return (
        <Select 
          size="small" 
          fullWidth 
          value={currentValue} 
          onChange={async (e) => {
            const val = e.target.value;
            setCurrentValue(val);
            setIsEditing(false);
            if (val != (value || "")) {
              await onSave(field, val);
            }
          }}
          onBlur={() => setIsEditing(false)}
          autoFocus
        >
          {options.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
        </Select>
      );
    }
    return (
      <TextField 
        size="small" 
        fullWidth 
        autoFocus 
        type={type}
        value={currentValue} 
        onChange={e => setCurrentValue(e.target.value)} 
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
    );
  }

  return (
    <Box 
      onDoubleClick={() => setIsEditing(true)} 
      sx={{ 
        cursor: "text", 
        minHeight: "24px", 
        padding: "4px", 
        borderRadius: "4px",
        border: "1px solid transparent", 
        "&:hover": { border: "1px dashed #ccc", backgroundColor: "#f9f9f9" } 
      }}
      title="Double click to edit"
    >
      <Typography variant="body1" sx={{ wordBreak: "break-all" }}>
        {displayValue !== undefined ? displayValue : (currentValue || "N/A")}
      </Typography>
    </Box>
  );
};

const CampUrlCard = ({ linkData, codeType, onSave, onDuplicate }) => {
  const [isEditingAll, setIsEditingAll] = useState(false);

  const handleFieldSave = async (field, value) => {
    const partialUpdate = { id: linkData.id, [field]: value };
    await onSave(partialUpdate);
  };

  const typeOptions = codeType?.map(item => ({ value: item.type_id, label: `${item.type_id}-${item.description}` })) || [];
  const foundType = codeType?.find(t => t.type_id == linkData.type);
  const typeDisplay = foundType ? `${foundType.type_id}-${foundType.description}` : linkData.type || "N/A";

  return (
    <Card 
      variant="outlined" 
      sx={{ 
        borderRadius: "12px", 
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        transition: "0.2s",
        "&:hover": { boxShadow: "0 6px 12px rgba(0,0,0,0.12)" }
      }}
    >
      <Box sx={{ padding: "10px 14px", backgroundColor: "#ffffff", borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ color: "#333", display: "flex", alignItems: "center", lineHeight: 1.2 }}>
          {linkData.site_name ? `${linkData.site_name} (${linkData.store_id}) | Link ID: ${linkData.id}` : `Store: ${linkData.store_id} | Link ID: ${linkData.id}`}
          <EditIcon 
            sx={{ fontSize: 16, ml: 1, color: isEditingAll ? "primary.main" : "text.secondary", cursor: "pointer", "&:hover": { color: "primary.main" } }} 
            titleAccess="Toggle edit mode for all fields"
            onClick={() => setIsEditingAll(!isEditingAll)}
          />
          <ContentCopyIcon 
            sx={{ fontSize: 16, ml: 1, color: "text.secondary", cursor: "pointer", "&:hover": { color: "primary.main" } }} 
            titleAccess="Duplicate this link"
            onClick={() => onDuplicate(linkData)}
          />
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {linkData.percentShare > 0 && (
            <Chip size="small" color="primary" label={`${linkData.percentShare}% Share`} />
          )}
          <Typography variant="body2" fontWeight="500">Active</Typography>
          <Switch 
            checked={linkData.active == 1} 
            onChange={(e) => handleFieldSave("active", e.target.checked ? 1 : 0)} 
            color="success" 
            size="small" 
          />
        </Box>
      </Box>
      <CardContent sx={{ padding: "10px 14px", "&:last-child": { paddingBottom: "10px" } }}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary">Link:</Typography>
            <EditableField field="link" value={linkData.link} forceEdit={isEditingAll} onSave={handleFieldSave} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary">Link 2:</Typography>
            <EditableField field="link2" value={linkData.link2} forceEdit={isEditingAll} onSave={handleFieldSave} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary">Targeted Countries:</Typography>
            <EditableField field="targeted_countries" value={linkData.targeted_countries} forceEdit={isEditingAll} onSave={handleFieldSave} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary">Device Type:</Typography>
            <EditableField field="device_type" value={linkData.device_type} forceEdit={isEditingAll} onSave={handleFieldSave} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="textSecondary">Ratio:</Typography>
            <EditableField field="ratio" type="number" value={linkData.ratio} forceEdit={isEditingAll} onSave={handleFieldSave} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="textSecondary">Type:</Typography>
            <EditableField field="type" type="select" value={linkData.type} options={typeOptions} displayValue={typeDisplay} forceEdit={isEditingAll} onSave={handleFieldSave} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="textSecondary">Comments:</Typography>
            <EditableField field="comments" value={linkData.comments} forceEdit={isEditingAll} onSave={handleFieldSave} />
          </Grid>
          {linkData.created_on && (
            <Grid item xs={6}>
              <Typography variant="caption" color="textSecondary">Created: {new Date(linkData.created_on).toLocaleDateString()}</Typography>
            </Grid>
          )}
          {linkData.last_updated_on && (
            <Grid item xs={6} sx={{ textAlign: "right" }}>
              <Typography variant="caption" color="textSecondary">Updated: {new Date(linkData.last_updated_on).toLocaleString()}</Typography>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

const CampURLs = () => {
  const dispatch = useDispatch();
  const allCampUrlsData = useSelector((state) => state.clicks.campUrlsData);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const siteQuery = searchParams.get("site");
  const storeIdQuery = searchParams.get("store_id");

  const [page, setPage] = useState(1);
  const [itemsPerPage] = useState(24);
  const [showInput, setShowInput] = useState(false);
  const [duplicateData, setDuplicateData] = useState(null);
  const [siteId, setSiteId] = useState();
  const [codeType, setCodeType] = useState();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch(getCampUrls()).then((data) => {
          if (data.payload?.code !== 200 && data.payload?.code) {
             // suppress success toast for quiet loading
          }
        });
        dispatch(getSiteId()).then((data) => {
          setSiteId(data.payload);
        });
        dispatch(getCodeType()).then((data) => {
          setCodeType(data.payload);
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [dispatch]);

  // Filtering
  let filteredData = allCampUrlsData || [];
  if (storeIdQuery) {
    filteredData = filteredData.filter(d => d.store_id == storeIdQuery);
  } else if (siteQuery) {
    filteredData = filteredData.filter(d => d.site_name?.toLowerCase().includes(siteQuery.toLowerCase()));
  }

  // Local Search Filtering
  if (searchTerm) {
    filteredData = filteredData.filter(d => 
      (d.site_name && d.site_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (d.link && d.link.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (d.comments && d.comments.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (d.id && d.id.toString() === searchTerm) ||
      (d.store_id && d.store_id.toString() === searchTerm)
    );
  }

  // Active Filter
  if (activeFilter !== "all") {
    filteredData = filteredData.filter(d => 
      activeFilter === "active" ? d.active == 1 : d.active == 0
    );
  }

  // Calculating Ratio share partitioned by store_id, targeted_countries, and device_type
  const partitionRatios = {};
  (allCampUrlsData || []).forEach(d => {
    if (d.active == 1) {
      const key = `${d.store_id}_${d.targeted_countries || ''}_${d.device_type || ''}`;
      if (!partitionRatios[key]) partitionRatios[key] = 0;
      partitionRatios[key] += Number(d.ratio || 0);
    }
  });

  const enhancedData = filteredData.map(d => {
    let percentShare = 0;
    const key = `${d.store_id}_${d.targeted_countries || ''}_${d.device_type || ''}`;
    const totalActiveRatio = partitionRatios[key] || 0;
    if (d.active == 1 && totalActiveRatio > 0) {
      percentShare = ((Number(d.ratio || 0) / totalActiveRatio) * 100).toFixed(2);
    }
    return { ...d, percentShare };
  });

  // Sort by active
  enhancedData.sort((a, b) => b.active - a.active);

  const totalLinks = enhancedData.length;
  const activeLinks = enhancedData.filter(d => d.active == 1).length;

  const totalPages = Math.ceil(totalLinks / itemsPerPage);
  const currentData = enhancedData.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleDuplicate = (data) => {
    setDuplicateData(data);
    setShowInput(true);
    // Scroll to top where the form is
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSaveLink = async (formData) => {
    try {
      await dispatch(updateCampUrls(formData));
      dispatch(getCampUrls());
      toast.success("Updated Successfully!", { position: "top-center", autoClose: 1500 });
    } catch (e) {
      toast.error("Error updating link", { position: "top-center", autoClose: 3000 });
    }
  };

  return (
    <Box sx={{ padding: { xs: "8px", sm: "12px" }, minHeight: "100vh", backgroundColor: "#f4f7f9" }}>
      <Helmet>
        <title>Alpha Targeting || Campaign Links</title>
      </Helmet>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Typography variant="h5" fontWeight="bold" color="primary.main">
          Campaign Links {siteQuery ? `- ${siteQuery}` : ""} {storeIdQuery ? `(ID: ${storeIdQuery})` : ""}
        </Typography>
        <Box>
          <Chip label={`Total: ${totalLinks}`} color="primary" variant="outlined" sx={{ mr: 1, fontSize: '1rem', fontWeight: 'bold' }} />
          <Chip label={`Active: ${activeLinks}`} color="success" variant="outlined" sx={{ fontSize: '1rem', fontWeight: 'bold' }} />
        </Box>
      </Box>

      <Paper sx={{ padding: "12px", marginBottom: "16px", borderRadius: "10px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search Links, Site Name, ID"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={activeFilter}
                onChange={(e) => {
                  setActiveFilter(e.target.value);
                  setPage(1);
                }}
                label="Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5} sx={{ textAlign: "right" }}>
            <Button size="small" variant="contained" color="primary" onClick={() => { setShowInput(!showInput); setDuplicateData(null); }}>
              {showInput ? "Cancel" : "Add Link"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {showInput && (
        <Paper sx={{ p: 1.5, mb: 2, borderRadius: "10px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
          <AddRow codeType={codeType} siteId={siteId} setShowInput={setShowInput} duplicateData={duplicateData} />
        </Paper>
      )}

      <Grid container spacing={1.5}>
        {currentData.map((linkData) => (
          <Grid item xs={12} lg={6} key={linkData.id}>
            <CampUrlCard linkData={linkData} codeType={codeType} onSave={handleSaveLink} onDuplicate={handleDuplicate} />
          </Grid>
        ))}
        {currentData.length === 0 && (
          <Grid item xs={12}>
            <Typography variant="h6" color="textSecondary" align="center" sx={{ mt: 5 }}>
              No links found.
            </Typography>
          </Grid>
        )}
      </Grid>

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}
    </Box>
  );
};

export default CampURLs;
